export const REVIEW_NOTE_PLACEHOLDER_EXAMPLES = Object.freeze({
  overall: Object.freeze([
    "e.g. This was the first flight after the annual inspection, and the engine felt normal throughout.",
    "e.g. The flight included several deliberate power changes while I checked engine response.",
    "e.g. No unusual vibration, roughness, odor, or cockpit indications were noticed.",
  ]),
  cylinder: Object.freeze([
    "e.g. The spark plugs for this cylinder were cleaned and rotated during the last service.",
    "e.g. This cylinder’s temperature probe was removed and reinstalled during the annual.",
    "e.g. I noticed brief roughness during runup, but it cleared before takeoff.",
  ]),
  sensors: Object.freeze([
    "e.g. One probe connector was cleaned and reseated during recent maintenance.",
    "e.g. The engine monitor briefly showed an intermittent temperature reading on the previous flight.",
    "e.g. Wiring behind the panel was disturbed during avionics work last week.",
  ]),
  instrumentationSuspicion: Object.freeze([
    "e.g. The engine monitor ground connection was checked during recent avionics work.",
    "e.g. Several temperature channels flickered together once during taxi last month.",
    "e.g. The probe harness connector was unplugged and reconnected at the last inspection.",
  ]),
  turbo: Object.freeze([
    "e.g. This was the first flight after turbocharger controller maintenance.",
    "e.g. The wastegate linkage was inspected and lubricated during the last service.",
    "e.g. Boost felt slower to build than usual during a recent climb.",
  ]),
  oilSystem: Object.freeze([
    "e.g. The oil and filter were changed shortly before this flight.",
    "e.g. One quart of oil was added before departure.",
    "e.g. The oil temperature probe was replaced during the last annual.",
  ]),
  coolantMargin: Object.freeze([
    "e.g. Coolant was topped off after the previous flight.",
    "e.g. The radiator was partially blocked for cold-weather operation.",
    "e.g. The coolant temperature sensor was replaced during recent maintenance.",
  ]),
  takeoffEnrichment: Object.freeze([
    "e.g. The fuel system was adjusted during recent maintenance.",
    "e.g. Mixture was set full rich before takeoff from a high-elevation airport.",
    "e.g. This was the first full-power takeoff after the fuel injectors were cleaned.",
  ]),
  cht: Object.freeze([
    "e.g. A baffle seal was repositioned during the last inspection.",
    "e.g. The climb was longer and slower than I normally fly on a hot afternoon.",
    "e.g. The cowling was recently removed and reinstalled.",
  ]),
  egt: Object.freeze([
    "e.g. The fuel injectors were cleaned and flow-tested during the last service.",
    "e.g. One spark plug was replaced after the previous flight.",
    "e.g. I leaned manually by feel instead of using the monitor’s peak function.",
  ]),
  valve: Object.freeze([
    "e.g. The exhaust valves were inspected during the last annual.",
    "e.g. One cylinder recently had a compression check and borescope inspection.",
    "e.g. A subtle roughness has occasionally appeared during cruise.",
  ]),
  abnormalCombustion: Object.freeze([
    "e.g. A brief stumble was felt after a rapid power increase.",
    "e.g. The spark plugs were serviced after the previous flight.",
    "e.g. Fuel from a different supplier was added before departure.",
  ]),
  inductionLeak: Object.freeze([
    "e.g. The intake tube seals were replaced during the last annual.",
    "e.g. An intake runner was removed and reinstalled during recent maintenance.",
    "e.g. Idle has felt slightly rough since the last maintenance visit.",
  ]),
  carbIce: Object.freeze([
    "e.g. Light moisture was visible on the windshield during taxi.",
    "e.g. Carb heat was applied briefly when the engine began to feel rough.",
    "e.g. The carb heat cable was adjusted during the last annual.",
  ]),
  cooling: Object.freeze([
    "e.g. Several baffle seals were replaced during the last inspection.",
    "e.g. The cowling was recently removed and reinstalled.",
    "e.g. I normally climb with a little more airspeed than I used on this flight.",
  ]),
  coolingRate: Object.freeze([
    "e.g. Power was reduced quickly before beginning the descent.",
    "e.g. The descent was steeper than I normally fly.",
    "e.g. Outside air was noticeably colder below the cloud layer.",
  ]),
  charging: Object.freeze([
    "e.g. The aircraft sat unplugged in hot weather for several days before this flight.",
    "e.g. The starter sounded slower than usual on the first start of the day.",
    "e.g. The battery had been run down during avionics work earlier in the week.",
  ]),
  electrical: Object.freeze([
    "e.g. Several high-draw accessories were turned on together shortly after start.",
    "e.g. The alternator belt tension was checked and adjusted during recent maintenance.",
    "e.g. The low-voltage light flickered briefly on a previous flight.",
  ]),
  engineDelta: Object.freeze([
    "e.g. The left engine recently had injector work; the right engine did not.",
    "e.g. The two engines have different amounts of time since overhaul.",
    "e.g. One propeller was recently balanced.",
  ]),
  mixtureContext: Object.freeze([
    "e.g. I leaned slowly through the peaks and then adjusted the mixture for smoothness.",
    "e.g. Mixture was adjusted to a fuel-flow target without looking for peak EGT.",
    "e.g. I stopped leaning when the engine began to feel slightly rough.",
  ]),
  fuelFlowResponse: Object.freeze([
    "e.g. The throttle was advanced gradually instead of all at once.",
    "e.g. Mixture and throttle were adjusted close together during the climb.",
    "e.g. The fuel-flow transducer was serviced during recent maintenance.",
  ]),
  combustion: Object.freeze([
    "e.g. One injector was cleaned after uneven spark-plug color was found.",
    "e.g. A spark plug was recently replaced on one cylinder.",
    "e.g. Brief roughness was felt immediately after a mixture adjustment.",
  ]),
  cylinderHarmony: Object.freeze([
    "e.g. The fuel injectors were flow-balanced during the last service.",
    "e.g. The spark plugs were cleaned and rotated before this flight.",
    "e.g. The engine felt smoother after the mixture settled during cruise.",
  ]),
  chtRhythm: Object.freeze([
    "e.g. One CHT probe connector was recently cleaned and reseated.",
    "e.g. Baffle material near one cylinder was repaired during the annual.",
    "e.g. I have noticed small repeating CHT movements on previous flights.",
  ]),
  divergence: Object.freeze([
    "e.g. The throttle and mixture were moved close together during the power change.",
    "e.g. A brief roughness was felt immediately after reducing power.",
    "e.g. One fuel injector was replaced during recent maintenance.",
  ]),
  descentEgtDropout: Object.freeze([
    "e.g. The mixture was enriched before beginning the descent.",
    "e.g. Power was reduced quickly when entering the descent.",
    "e.g. A brief stumble was felt during a low-power descent on a previous flight.",
  ]),
  mag: Object.freeze([
    "e.g. The runup was started before the engine was fully warm.",
    "e.g. The spark plugs were cleaned and rotated before this flight.",
    "e.g. The left-mag drop has seemed slightly larger during the last few runups.",
  ]),
});

export const GENERIC_REVIEW_NOTE_PLACEHOLDER_EXAMPLES = Object.freeze([
  "e.g. This was the first flight after recent maintenance.",
  "e.g. I noticed something similar on the previous flight.",
  "e.g. Nothing unusual was noticed from the cockpit.",
]);

type ReviewNotePlaceholderKey = keyof typeof REVIEW_NOTE_PLACEHOLDER_EXAMPLES;
type RandomSource = () => number;

export function reviewNotePlaceholderExamples(cueKey: unknown): readonly string[] {
  const key = String(cueKey ?? "").trim();
  if (Object.hasOwn(REVIEW_NOTE_PLACEHOLDER_EXAMPLES, key)) {
    return REVIEW_NOTE_PLACEHOLDER_EXAMPLES[key as ReviewNotePlaceholderKey];
  }
  return GENERIC_REVIEW_NOTE_PLACEHOLDER_EXAMPLES;
}

// Placeholders are static fictional examples. The picker receives no flight or
// detector data and caches one random choice per cue for the current page
// session, preventing ordinary dashboard rerenders from changing the copy.
export function createReviewNotePlaceholderPicker(randomSource: RandomSource = Math.random) {
  const selectedByCueKey = new Map<string, string>();
  return (cueKey: unknown): string => {
    const key = String(cueKey ?? "").trim();
    const cacheKey = `cue:${key}`;
    const selected = selectedByCueKey.get(cacheKey);
    if (selected) return selected;

    const examples = reviewNotePlaceholderExamples(key);
    const randomValue = Number(randomSource());
    const rawIndex = Number.isFinite(randomValue)
      ? Math.floor(randomValue * examples.length)
      : 0;
    const index = Math.min(examples.length - 1, Math.max(0, rawIndex));
    const placeholder = examples[index];
    selectedByCueKey.set(cacheKey, placeholder);
    return placeholder;
  };
}
